/* eslint-disable react/no-danger */
import { ReactElement, useRef, useState, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import { StarOutlined } from 'assets/icons/icons';
import { Button, Tooltip, Typography, theme } from 'antd';
import { VendorOfferType } from 'app/types/vendors/vendors.types';
import API from 'app/utils/api/axios';
import 'app/components/elements/cards/vendor_offers_card/vendor_offers_card.scss';

type OfferCardType = Omit<VendorOfferType, 'claim_mode' | 'claim_instructions'> & {
    mode: 'quote' | 'instructions';
    toggleModal: (pk: number, mode: 'quote' | 'instructions' | 'payments') => void;
    isProMember: boolean;
    isNew?: boolean;
};

const VendorOffersCard = ({
    pk,
    title,
    content,
    image,
    listing_url,
    sponsor,
    premium,
    mode,
    isProMember,
    cta,
    cta_url,
    isNew,
    toggleModal,
}: OfferCardType): ReactElement => {
    const { token } = theme.useToken();
    const [loading, setLoading] = useState<boolean>(false);
    const [isModalFirstToggled, setIsModalFirstToggled] = useState<boolean>(true);
    const params = useParams();
    const cardRef = useRef<HTMLDivElement>(null);
    const isSelected = params.pk === pk.toString();
    const isLocked = premium && !isProMember;

    const asyncOfferTracking = async (): Promise<void> => {
        await API.get(`/api/offers/${pk}/view/`);
    };

    const renderCheckListingUrl = (): JSX.Element =>
        listing_url ? (
            <a href={listing_url} target='_blank' rel='noreferrer' className='link'>
                {sponsor}
            </a>
        ) : (
            <Typography.Text>{sponsor}</Typography.Text>
        );

    const renderClaimOfferButton = (): JSX.Element => (
        <Button
            className='ant-btn-primary-outline'
            type='default'
            data-cy={
                cta && cta.toLowerCase().includes('quote')
                    ? 'quote-vendor-button'
                    : 'claim-vendor-button'
            }
            onClick={() => {
                asyncOfferTracking();
                return cta_url?.length
                    ? window.open(cta_url, '_blank', 'noopener')
                    : toggleModal(pk, isLocked ? 'payments' : mode);
            }}
        >
            {cta || 'Claim offer'}
        </Button>
    );

    useLayoutEffect(() => {
        if (isSelected && isModalFirstToggled) {
            cardRef.current?.focus();
            cardRef.current?.scrollIntoView({
                block: 'center',
                behavior: 'smooth',
            });

            if (isLocked) {
                toggleModal(pk, 'payments');
            } else if (!cta_url?.length) {
                toggleModal(pk, mode);
            }

            setIsModalFirstToggled(false);
        }
    }, [isSelected, toggleModal, pk, isModalFirstToggled, cta_url, isLocked, mode]);

    return (
        <div
            ref={cardRef}
            className={`offer-card ${isSelected ? 'isActive' : ''}`}
            data-offer-premium={Boolean(premium)}
            style={{ borderRadius: token.borderRadius }}
        >
            <div className='offer-card__logo' style={{ borderRadius: token.borderRadius }}>
                {image && (
                    <img
                        src={image}
                        alt={title}
                        loading='lazy'
                        onLoad={() => setLoading(true)}
                        className={`load-image ${loading ? 'image-loaded' : ''}`}
                    />
                )}
                {isLocked && (
                    <Tooltip title='This offer is only available to Pro members' placement='bottom'>
                        <span className='offer-card__premium-badge'>
                            <StarOutlined width='1.4rem' /> Pro
                        </span>
                    </Tooltip>
                )}
            </div>
            <div className='offer-card__info'>
                <div className='offer-card__header'>
                    <div className='offer-card__title-wrap'>
                        <Typography.Title level={5} className='offer-card__subtitle'>
                            {renderCheckListingUrl()}
                        </Typography.Title>

                        <Tooltip title={title} placement='bottom'>
                            <Typography.Title level={5} className='offer-card__title'>
                                {title}
                                {isNew && (
                                    <span className='side-navigation__list-item-tag offer-card__tag'>
                                        new
                                    </span>
                                )}
                            </Typography.Title>
                        </Tooltip>
                    </div>
                    <div
                        className='offer-card__description'
                        dangerouslySetInnerHTML={{
                            __html: content,
                        }}
                    />
                </div>

                <div className='offer-card__actions'>
                    <div className='offer-card__actions-wrap'>{renderClaimOfferButton()}</div>
                </div>
            </div>
        </div>
    );
};

export default VendorOffersCard;
